/**
 * Utilitário para converter URLs WAHA com autenticação para URLs de proxy
 *
 * Problema: URLs como "http://localhost:3000/api/files/clinic68/arquivo.oga"
 * exigem autenticação X-Api-Key que o navegador não pode enviar.
 *
 * Solução: Converter para "/api/waha/media/clinic68/arquivo.oga"
 * que usa o proxy do backend com autenticação.
 */

/**
 * Converte URL do WAHA para URL de proxy autenticado
 * @param url Original URL (pode ser WAHA ou outra)
 * @returns URL convertida para proxy se for WAHA, senão URL original
 */
function getStoredClinicId(): string | null {
  if (typeof window === 'undefined') return null;
  const clinicId = localStorage.getItem('clinic_id');
  return clinicId && /^\d+$/.test(clinicId) ? clinicId : null;
}

/** Acrescenta o contexto da clínica sem transformar a URL em cross-origin. */
export function withWahaClinicContext(url: string, clinicId?: number | string | null): string {
  if (!url.startsWith('/api/waha/media/')) return url;

  const resolvedClinicId = clinicId ? String(clinicId) : getStoredClinicId();
  if (!resolvedClinicId || !/^\d+$/.test(resolvedClinicId)) return url;

  const [pathAndQuery, hash = ''] = url.split('#', 2);
  const separator = pathAndQuery.includes('?') ? '&' : '?';
  if (/[?&]clinic_id=/.test(pathAndQuery)) return url;
  return `${pathAndQuery}${separator}clinic_id=${encodeURIComponent(resolvedClinicId)}${hash ? `#${hash}` : ''}`;
}

export function convertWahaUrlToProxy(
  url: string,
  clinicId?: number | string | null,
): string {
  if (!url || typeof url !== 'string') {
    return url;
  }

  let normalizedUrl = url.trim();

  // Links persistidos em app/dev devem acompanhar o host atual. Isso evita que
  // uma sessão aberta em dev tente enviar seu cookie para app (ou vice-versa).
  if (/^https?:\/\//i.test(normalizedUrl)) {
    try {
      const parsedUrl = new URL(normalizedUrl);
      if (
        ['app.sminy.ai', 'dev.sminy.ai'].includes(parsedUrl.hostname) &&
        parsedUrl.pathname.startsWith('/api/waha/media/')
      ) {
        normalizedUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      }
    } catch {
      // Mantém o valor original para URLs malformadas; o chamador tratará o erro.
    }
  }

  // 🔥 FIX: Se já for URL de proxy, não converter novamente
  if (normalizedUrl.startsWith('/api/waha/media/')) {
    return withWahaClinicContext(normalizedUrl, clinicId);
  }

  // Verificar se é URL do WAHA que precisa de proxy
  if (isWahaUrl(normalizedUrl)) {
    // Extrair sessão WAHA e filename da URL.
    // Formatos reais:
    // - http://localhost:3000/api/files/clinic68/arquivo.oga
    // - http://localhost:3000/api/files/vilar/arquivo.mp4
    // - http://localhost:3000/api/files/SelectaOdontologiaAgatha/arquivo.oga
    const match = normalizedUrl.match(/(?:localhost|127\.0\.0\.1):3000\/api\/files\/([^/?#]+)\/([^?#]+)/i);

    if (match) {
      const sessionOrClinic = match[1];
      const filename = match[2].replace(/^\/+/, '');
      const proxyUrl = `/api/waha/media/${sessionOrClinic}/${filename}`;

      return withWahaClinicContext(proxyUrl, clinicId);
    }
  }

  // Se não for URL WAHA, retornar original
  return normalizedUrl;
}

/**
 * Verifica se uma URL é do WAHA e precisa de proxy
 * @param url URL para verificar
 * @returns true se for URL WAHA que precisa de proxy
 */
export function isWahaUrl(url: string): boolean {
  return !!url && /(?:localhost|127\.0\.0\.1):3000\/api\/files\//i.test(url);
}

/**
 * Processa objeto de conteúdo de mensagem para converter URLs WAHA
 * @param content Conteúdo da mensagem (string ou objeto)
 * @returns Conteúdo com URLs WAHA convertidas para proxy
 */
export function processMessageContent(content: any): any {
  if (!content) return content;

  // Se for string, converter diretamente
  if (typeof content === 'string') {
    return convertWahaUrlToProxy(content);
  }

  // Se for objeto, processar recursivamente
  if (typeof content === 'object') {
    const processed = { ...content };

    // Campos comuns que podem conter URLs WAHA
    const urlFields = ['url', 'audioUrl', 'videoUrl', 'imageUrl', 'mediaUrl'];

    for (const field of urlFields) {
      if (processed[field]) {
        processed[field] = convertWahaUrlToProxy(processed[field]);
      }
    }

    return processed;
  }

  return content;
}
